import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { useAuthContext } from "@/contexts/AuthContext";
import type { SubsidyOffice, SubsidyOfficeListParams, SubsidyOfficeListResponse } from "@/types/subsidyOffice";

export const subsidyOfficeKeys = {
  all: ["subsidy-offices"] as const,
  lists: () => [...subsidyOfficeKeys.all, "list"] as const,
  // orgId is part of the key so a tenant switch can't reuse another org's
  // office list (which would surface invalid move targets / leak data).
  list: (params: SubsidyOfficeListParams & { orgId?: string | null }) =>
    [...subsidyOfficeKeys.lists(), params] as const,
  details: () => [...subsidyOfficeKeys.all, "detail"] as const,
  detail: (id: string, orgId?: string | null) =>
    [...subsidyOfficeKeys.details(), orgId ?? null, id] as const,
};

export function useSubsidyOffices(params: SubsidyOfficeListParams = {}) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  const { limit = 100, offset = 0, search, includeDeleted } = params;
  return useQuery({
    queryKey: subsidyOfficeKeys.list({ orgId, limit, offset, search, includeDeleted }),
    queryFn: async (): Promise<SubsidyOfficeListResponse> => {
      const { SubsidyOfficesService } = await import("@/api");
      const response = await SubsidyOfficesService.getApiV1SubsidyOffices(
        limit,
        offset,
        search,
        includeDeleted,
      );
      return response as SubsidyOfficeListResponse;
    },
    enabled: isSignedIn && isAuthReady && !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSubsidyOffice(id: string | undefined) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  return useQuery({
    queryKey: subsidyOfficeKeys.detail(id ?? "", orgId),
    queryFn: async (): Promise<SubsidyOffice> => {
      const { SubsidyOfficesService } = await import("@/api");
      const response = await SubsidyOfficesService.getApiV1SubsidyOffices1(id as string);
      return response as SubsidyOffice;
    },
    enabled: isSignedIn && isAuthReady && !!orgId && !!id,
    staleTime: 5 * 60 * 1000,
  });
}
